import { useRef, useState } from "react";
import SaveOutlined from "@mui/icons-material/SaveOutlined";
import { RotatingArrowIcon, SaveDraftButton, SaveDraftMenu } from "./SaveDraft.styles";
import {
	SaveAsDraftText,
	SaveAsTemplateText,
	SaveDraftHangTightText,
	SaveDraftSuccessfullySavedText,
	SaveTemplateSuccessfullySavedText
} from "../constants";
import SaveDraftModal from "./SaveDraftModal";
import { CancelButtonText, SaveButtonText, SaveDraftButtonText } from "constants/text";
import { models } from "types/api/viewModels.ts";
import { MenuItem } from "@mui/material";
import { useSaveDraftOrderMutation } from "features/api/orderApi.ts";
import { useDispatch } from "react-redux";
import { newOrderActions } from "features/reducers/newOrder/newOrder.ts";
import Snackbar from "components/Common/SnackBar/SnackBar";
import useSnackbarEffect from "hooks/useSnackbarEffect";
import { DraftType, DraftTypeType } from "types/declarations/enums.ts";

interface Props {
	draftOrder?: models["DraftOrderViewModel"];
	isAddOn?: boolean;
	isCustomColor?: boolean;
}

const SaveDraft = ({ draftOrder, isAddOn, isCustomColor }: Props) => {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [draftType, setDraftType] = useState(DraftType.Draft);
	const [isManuallySavingDraft, setIsManuallySavingDraft] = useState(false);
	const [saveDraftOrder, { isLoading: isSavingDraft }] = useSaveDraftOrderMutation();
	const [saveDraftErrorMessage, setSaveDraftErrorMessage] = useState<string | undefined>(undefined);
	const { snackbarOpen: draftSnackbarOpen } = useSnackbarEffect(
		isSavingDraft && isManuallySavingDraft,
		saveDraftErrorMessage
	);
	const dispatch = useDispatch();

	const handleMenuOpen = () => {
		if (isAddOn) {
			setIsModalOpen(true);
		} else {
			setIsMenuOpen(true);
		}
	};

	const handleModalOpen = (draftType: DraftTypeType) => {
		setIsModalOpen(true);
		setDraftType(draftType);
		setIsMenuOpen(false);
	};

	const handleClose = () => {
		setIsMenuOpen(false);
		setIsModalOpen(false);
	};

	const handleSaveDraft = async (draft: models["DraftOrderViewModel"]) => {
		setIsManuallySavingDraft(true);
		try {
			setSaveDraftErrorMessage(undefined);
			await saveDraftOrder(draft).unwrap();
			dispatch(newOrderActions.clearDraftError());
			// Reset the manual saving flag after a delay
			setTimeout(() => {
				setIsManuallySavingDraft(false);
			}, 2000);
		} catch (error: any) {
			console.error("Save draft failed:", error);
			setSaveDraftErrorMessage("Failed to save draft. Please try again.");
			dispatch(newOrderActions.updateDraftError(error));
			setIsManuallySavingDraft(false);
		}
	};

	const anchorRef = useRef(null);

	return (
		<>
			<SaveDraftButton
				variant="text"
				onClick={handleMenuOpen}
				data-testid="save-draft-template-button"
				ref={anchorRef}
			>
				<SaveOutlined />
				{isAddOn ? SaveDraftButtonText : SaveButtonText}
				{!isAddOn && <RotatingArrowIcon open={isMenuOpen} />}
			</SaveDraftButton>
			<SaveDraftMenu
				open={isMenuOpen}
				anchorEl={anchorRef.current}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left"
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "left"
				}}
				data-test-id="save-draft-menu"
			>
				<MenuItem
					onClick={() => handleModalOpen(DraftType.Draft)}
					data-testid="save-draft-menu-item"
				>
					{SaveAsDraftText}
				</MenuItem>
				{!isAddOn && !isCustomColor && (
					<MenuItem
						onClick={() => handleModalOpen(DraftType.Template)}
						data-testid="save-template-menu-item"
					>
						{SaveAsTemplateText}
					</MenuItem>
				)}
			</SaveDraftMenu>
			<SaveDraftModal
				draftOrder={draftOrder}
				closeModal={handleClose}
				saveDraftModalIsOpen={isModalOpen}
				firstButtonText={SaveButtonText}
				secondButtonText={CancelButtonText}
				draftType={draftType}
				onSubmit={handleSaveDraft}
			/>
			<Snackbar
				open={draftSnackbarOpen}
				isLoading={isSavingDraft}
				loadingText={SaveDraftHangTightText}
				successText={
					draftType === DraftType.Template
						? SaveTemplateSuccessfullySavedText
						: SaveDraftSuccessfullySavedText
				}
				isError={saveDraftErrorMessage !== undefined}
				errorText={saveDraftErrorMessage ?? ""}
			/>
		</>
	);
};

export default SaveDraft;
